import React, { createContext, useContext, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";

/**
 * Entrada del historial de navegación con estado completo
 */
interface NavigationEntry {
  path: string;
  label: string;
  state?: Record<string, any>;
  timestamp: number;
}

interface NavigationHistoryContextType {
  history: NavigationEntry[];
  pushToHistory: (
    path: string,
    label: string,
    state?: Record<string, any>,
  ) => void;
  popFromHistory: () => void;
  navigateToHistoryIndex: (index: number) => void;
  clearHistory: () => void;
  updateCurrentState: (state: Record<string, any>) => void;
}

const NavigationHistoryContext = createContext<
  NavigationHistoryContextType | undefined
>(undefined);

export function NavigationHistoryProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const [history, setHistory] = useState<NavigationEntry[]>([]);
  const navigate = useNavigate();

  // Función para agregar una entrada al historial
  const pushToHistory = useCallback(
    (path: string, label: string, state?: Record<string, any>) => {
      setHistory((prev) => {
        // Evitar duplicados consecutivos de la misma ruta
        const lastEntry = prev[prev.length - 1];
        if (prev.length > 0 && lastEntry && lastEntry.path === path) {
          // Actualizar el estado de la entrada existente
          const updated = [...prev];
          const currentEntry = updated[updated.length - 1];
          if (currentEntry) {
            updated[updated.length - 1] = {
              path: currentEntry.path,
              label: currentEntry.label,
              state: { ...currentEntry.state, ...state },
              timestamp: Date.now(),
            };
          }
          return updated;
        }

        // Agregar nueva entrada
        return [
          ...prev,
          {
            path,
            label,
            state: state || {},
            timestamp: Date.now(),
          },
        ];
      });
    },
    [],
  );

  // Función para regresar en el historial
  const popFromHistory = useCallback(() => {
    const previousEntry = history[history.length - 2];

    if (previousEntry) {
      // Navegar PRIMERO
      navigate(previousEntry.path, {
        state: previousEntry.state,
        replace: true,
      });

      // Luego actualizar el historial
      setHistory((prev) => {
        if (prev.length <= 1) return prev;
        return prev.slice(0, -1);
      });
    }
  }, [history, navigate]);

  // Función para navegar a un índice específico del historial
  const navigateToHistoryIndex = useCallback(
    (index: number) => {
      if (index < 0 || index >= history.length) return;

      const targetEntry = history[index];
      if (!targetEntry) return;

      // Navegar PRIMERO
      navigate(targetEntry.path, {
        state: targetEntry.state,
        replace: true,
      });

      // Luego actualizar el historial
      setHistory((prev) => prev.slice(0, index + 1));
    },
    [history, navigate],
  );

  // Función para limpiar el historial
  const clearHistory = useCallback(() => {
    setHistory([]);
  }, []);

  // Función para actualizar el estado de la entrada actual
  const updateCurrentState = useCallback((state: Record<string, any>) => {
    setHistory((prev) => {
      if (prev.length === 0) return prev;

      const updated = [...prev];
      const currentEntry = updated[updated.length - 1];

      if (currentEntry) {
        updated[updated.length - 1] = {
          path: currentEntry.path,
          label: currentEntry.label,
          state: { ...currentEntry.state, ...state },
          timestamp: currentEntry.timestamp,
        };
      }

      return updated;
    });
  }, []);

  const value: NavigationHistoryContextType = {
    history,
    pushToHistory,
    popFromHistory,
    navigateToHistoryIndex,
    clearHistory,
    updateCurrentState,
  };

  return (
    <NavigationHistoryContext.Provider value={value}>
      {children}
    </NavigationHistoryContext.Provider>
  );
}

export function useNavigationHistory(): NavigationHistoryContextType {
  const context = useContext(NavigationHistoryContext);
  if (!context) {
    throw new Error(
      "useNavigationHistory must be used within a NavigationHistoryProvider",
    );
  }
  return context;
}
